import { ChannelModel } from './ChannelModel'
import { cloneDeep } from 'lodash'
import { StoreModel } from '../../store/models/StoreModel';
import { ChannelAllowExportEnum, ChannelEntityTemplateDto, EntityEnum } from '@c2b/muven-commons';

interface StoreWithOnlyId {
    id: string|number|null;
}

interface ChannelAllowExportWrapper {
    enum: ChannelAllowExportEnum
    description: string
}

/** O que uma opção de sincronização faz com cada informação do catálogo no canal. */
export interface AllowExportMatrixRow {
    label: string
    inserts: boolean
    updates: boolean
}

export interface AllowExportDetail {
    enum: ChannelAllowExportEnum
    summary: string
    rows: AllowExportMatrixRow[]
}

const CATALOG_INFO = ['Nome do produto', 'Descrição do produto', 'Grade', 'SKU', 'Preço', 'Estoque', 'Dimensões', 'Imagens', 'Categoria', 'Marca']

// Uma letra por informação de CATALOG_INFO, na mesma ordem: S = sim, - = não
function buildMatrix(inserts: string, updates: string): AllowExportMatrixRow[] {
    return CATALOG_INFO.map((label, i) => ({label, inserts: inserts[i] === 'S', updates: updates[i] === 'S'}))
}

const ALLOW_EXPORT_DETAILS: AllowExportDetail[] = [
    {
        enum: ChannelAllowExportEnum.All,
        summary: 'Cadastra e atualiza todas as informações do catálogo',
        rows: buildMatrix('SSSSSSSSSS', 'SSSSSSSSSS'),
    },
    {
        enum: ChannelAllowExportEnum.StockAndPrice,
        summary: 'Não cadastra produtos · atualiza estoque e preço',
        rows: buildMatrix('----------', '----SS----'),
    },
    {
        enum: ChannelAllowExportEnum.Stock,
        summary: 'Não cadastra produtos · atualiza só o estoque',
        rows: buildMatrix('----------', '-----S----'),
    },
    {
        enum: ChannelAllowExportEnum.InsertProductAndUpdateStockAndPrice,
        summary: 'Cadastra produtos novos · atualiza estoque e preço',
        rows: buildMatrix('SSSSSSSS--', '----SS----'),
    },
    {
        enum: ChannelAllowExportEnum.InsertProductAndUpdateStock,
        summary: 'Cadastra produtos novos · atualiza só o estoque',
        rows: buildMatrix('SSSSSSSS--', '-----S----'),
    },
]

interface EntityTemplateFieldOption {
    enum: string;
    name: string;
    description: string;
}

interface EntityTemplateFieldOptionWrapper {
    [key: string]: EntityTemplateFieldOption;
}

const productEntityTemplateFields:EntityTemplateFieldOptionWrapper = {
    name: {
        enum: '{{name}}',
        name: '{nome_produto}',
        description: 'Nome do produto',
    },
    description: {
        enum: '{{description}}',
        name: '{descricao_produto}',
        description: 'Descrição principal do produto',
    },
    descriptionShort: {
        enum: '{{descriptionShort}}',
        name: '{descricao_curta_produto}',
        description: 'Descrição curta do produto',
    },
    descriptionLong: {
        enum: '{{descriptionLong}}',
        name: '{descricao_longa_produto}',
        description: 'Descrição longa do produto',
    },
    sellPrice: {
        enum: '{{sellPrice}}',
        name: '{preco_produto}',
        description: 'Preço de venda do produto',
    },
    weight: {
        enum: '{{weight}}',
        name: '{peso_produto}',
        description: 'Peso do produto em gramas',
    },
    height: {
        enum: '{{height}}',
        name: '{altura_produto}',
        description: 'Altura do produto em centímetros',
    },
    width: {
        enum: '{{width}}',
        name: '{largura_produto}',
        description: 'Largura do produto em centímetros',
    },
    depth: {
        enum: '{{depth}}',
        name: '{profundidade_produto}',
        description: 'Profundidade do produto em centímetros',
    },
    stockConversionRatio: {
        enum: '{{stockConversionRatio}}',
        name: '{fator_conversao_estoque}',
        description: 'Fator de conversão do estoque',
    },
    brandName: {
        enum: '{{brand.name}}',
        name: '{nome_marca}',
        description: 'Nome da marca',
    },
    seoTitle: {
        enum: '{{seoTitle}}',
        name: '{titulo_seo}',
        description: 'Título SEO',
    },
    seoDescription: {
        enum: '{{seoDescription}}',
        name: '{descricao_seo}',
        description: 'Descrição SEO',
    },
    seoKeywords: {
        enum: '{{seoKeywords}}',
        name: '{palavras_chave_seo}',
        description: 'Palavra chave SEO',
    },
}

export class ChannelSettingsFormModel {
    static getEntityTemplateFieldOptions():EntityTemplateFieldOption[] {
        return [
            productEntityTemplateFields.name,
            productEntityTemplateFields.description,
            productEntityTemplateFields.descriptionShort,
            productEntityTemplateFields.descriptionLong,
            productEntityTemplateFields.sellPrice,
            productEntityTemplateFields.weight,
            productEntityTemplateFields.height,
            productEntityTemplateFields.width,
            productEntityTemplateFields.depth,
            productEntityTemplateFields.stockConversionRatio,
            productEntityTemplateFields.brandName,
            productEntityTemplateFields.seoTitle,
            productEntityTemplateFields.seoDescription,
            productEntityTemplateFields.seoKeywords,
        ]
    }

    static getAllowExportOptions():ChannelAllowExportWrapper[] {
        return [
            {
                enum: ChannelAllowExportEnum.All,
                description: 'Exportar tudo',
            },
            {
                enum: ChannelAllowExportEnum.StockAndPrice,
                description: 'Exportar apenas estoque e preço',
            },
            {
                enum: ChannelAllowExportEnum.Stock,
                description: 'Exportar apenas estoque',
            },
            {
                enum: ChannelAllowExportEnum.InsertProductAndUpdateStockAndPrice,
                description: 'Exportar novos produtos e apenas atualizar preço e estoque',
            },
            {
                enum: ChannelAllowExportEnum.InsertProductAndUpdateStock,
                description: 'Exportar novos produtos e apenas atualizar estoque',
            },
        ]
    }

    static getAllowExportDetail(en: ChannelAllowExportEnum): AllowExportDetail | undefined {
        return ALLOW_EXPORT_DETAILS.find((d) => d.enum === en)
    }

    static normalizeBeforeSubmit(settingsForm: ChannelSettingsFormModel):ChannelSettingsFormModel {
        const tmp = cloneDeep(settingsForm)

        tmp.pricePercentage = tmp.pricePercentage || null

        if (!!tmp.availableStockPercentage) {
            if (tmp.availableStockPercentage > 100) {
                tmp.availableStockPercentage = 100

            } else {
                if (tmp.availableStockPercentage < 0) {
                    tmp.availableStockPercentage = 0
                }
            }
        }

        if (tmp.priceDecimalsPreferenceString) {
            tmp.priceDecimalsPreference = parseInt(tmp.priceDecimalsPreferenceString, 10);
        } else {
            tmp.priceDecimalsPreference = null
        }

        tmp.stockStores = (tmp.stockStores as StoreModel[]).map((store: StoreModel) => {
            return String(store.id)
        })

        tmp.priceManagementStore = {
            id: tmp.priceManagementStore?.id ? String(tmp.priceManagementStore.id) : null,
        }

        tmp.orderManagementStore = {
            id: tmp.orderManagementStore?.id ? String(tmp.orderManagementStore.id) : null,
        }

        tmp.allowExport = tmp.allowExportWrapper.enum

        Object.keys(tmp.productEntityTemplate!.fields).forEach((keyEntityTemplate) => {
            Object.keys(productEntityTemplateFields).forEach((keyEntityTemplateOption) => {
                tmp.productEntityTemplate!.fields[keyEntityTemplate] = tmp.productEntityTemplate!.fields[keyEntityTemplate]
                    .replace(productEntityTemplateFields[keyEntityTemplateOption].name, productEntityTemplateFields[keyEntityTemplateOption].enum)
            })

            tmp.productEntityTemplate!.fields[keyEntityTemplate] = tmp.productEntityTemplate!.fields[keyEntityTemplate].trim()
        })

        tmp.entityTemplate = [tmp.productEntityTemplate as ChannelEntityTemplateDto]

        return tmp
    }

    name: string = ""
    isActive: boolean = false
    allowExportWrapper: ChannelAllowExportWrapper = ChannelSettingsFormModel.getAllowExportOptions()[0]
    allowExport: ChannelAllowExportEnum = ChannelAllowExportEnum.All
    availableStockPercentage?: number
    pricePercentage?: number|null
    priceDecimalsPreference?: number|null
    priceDecimalsPreferenceString?: string
    stockStores: null|string[]|StoreModel[] = []
    priceManagementStore: null|StoreWithOnlyId|StoreModel = new StoreModel()
    orderManagementStore: null|StoreWithOnlyId|StoreModel = new StoreModel()
    entityTemplate: ChannelEntityTemplateDto[]|null = null        
    productEntityTemplate: ChannelEntityTemplateDto|null = null        
    showWithoutStock:boolean = false

    constructor(c?: Partial<ChannelModel>) {
        this.name = c?.name || ""
        this.allowExport = c?.allowExport ?? ChannelAllowExportEnum.All
        this.availableStockPercentage = c?.availableStockPercentage || 100
        this.pricePercentage = c?.pricePercentage || 100
        this.priceDecimalsPreference = c?.priceDecimalsPreference
        this.isActive = !!c?.isActive
        this.stockStores = c?.stockStores as string[]
        this.priceManagementStore = c?.priceManagementStore as StoreWithOnlyId
        this.orderManagementStore = c?.orderManagementStore as StoreWithOnlyId
        this.showWithoutStock = c?.showWithoutStock ?? false
        
        this._parsePriceDecimalsPreferenceString()
        this._parseStockStores()
        this._parsePriceManagementStore()
        this._parseOrderManagementStore()
        this._parseAllowExport()
        this._parseEntityTemplate(c)
    }

    hasPriceManagementStore():boolean {
        return !!this.priceManagementStore?.id;
    }

    hasStockStores():boolean {
        return !!this.stockStores?.length;
    }

    hasOrderManagementStore():boolean {
        return !!this.orderManagementStore?.id;
    }

    isPriceManagementStoreEqual(store: StoreWithOnlyId|null):boolean {
        return this.priceManagementStore?.id === store?.id;
    }

    isAllowExportAll():boolean {
        return this.allowExportWrapper.enum === ChannelAllowExportEnum.All
    }

    isAllowExportStock():boolean {
        return this.allowExportWrapper.enum === ChannelAllowExportEnum.Stock
    }

    isAllowExportStockAndPrice():boolean {
        return this.allowExportWrapper.enum === ChannelAllowExportEnum.StockAndPrice
    }

    isAllowExportInsertProductAndUpdateStockAndPrice():boolean {
        return this.allowExportWrapper.enum === ChannelAllowExportEnum.InsertProductAndUpdateStockAndPrice
    }

    isAllowExportInsertProductAndUpdateStock():boolean {
        return this.allowExportWrapper.enum === ChannelAllowExportEnum.InsertProductAndUpdateStock
    }

    /**
     * Está saindo de qualquer outra configuração para "Exportar tudo". Essa troca
     * pode sobrescrever o que já existe no canal, por isso exige confirmação.
     */
    isTurningIntoAllowExportAll(previousAllowExport:ChannelAllowExportEnum):boolean {
        return this.isAllowExportAll() && previousAllowExport !== ChannelAllowExportEnum.All
    }

    isEntityTemplateFieldNameValid():boolean {
        return this._productEntityTemplateFieldValidation('name')
    }

    isEntityTemplateFieldDescriptionValid():boolean {
        return this._productEntityTemplateFieldValidation('description')
    }

    isEntityTemplateFieldDescriptionShortValid():boolean {
        return this._productEntityTemplateFieldValidation('descriptionShort')
    }

    isEntityTemplateFieldDescriptionLongValid():boolean {
        return this._productEntityTemplateFieldValidation('descriptionLong')
    }

    isEntityTemplateFieldSeoTitleValid():boolean {
        return this._productEntityTemplateFieldValidation('seoTitle')
    }

    isEntityTemplateFieldSeoDescriptionValid():boolean {
        return this._productEntityTemplateFieldValidation('seoDescription')
    }

    isEntityTemplateFieldSeoKeywordsValid():boolean {
        return this._productEntityTemplateFieldValidation('seoKeywords')
    }

    _productEntityTemplateFieldValidation(prop: string):boolean {
        if (!this.productEntityTemplate?.fields?.[prop]) {
            return false
        }

        let result = false

        Object.keys(productEntityTemplateFields).forEach((keyFieldOption) => {
            if (result) {
                return
            }

            const everyVariableListInInput = this.productEntityTemplate!.fields[prop].match(/({\w+})/g) || []

            for (const v of everyVariableListInInput) {
                if (v.indexOf(productEntityTemplateFields[keyFieldOption].name) > -1) {
                    result = true
                } else {
                    result = false
                }
            }
        })

        return result
    }

    private _parsePriceDecimalsPreferenceString():void {
        if (typeof this.priceDecimalsPreference === "number") {
            this.priceDecimalsPreferenceString = this.priceDecimalsPreference < 10 ? `0${this.priceDecimalsPreference}` : `${this.priceDecimalsPreference}`
            return
        }
    }

    private _parseStockStores():void {
        this.stockStores = this.stockStores || []
    }

    private _parsePriceManagementStore():void {
        this.priceManagementStore = new StoreModel(this.priceManagementStore as StoreModel)
    }

    private _parseOrderManagementStore():void {
        this.orderManagementStore = new StoreModel(this.orderManagementStore as StoreModel)
    }

    private _parseAllowExport():void {
        this.allowExportWrapper = ChannelSettingsFormModel.getAllowExportOptions().find((o) => o.enum === this.allowExport) as ChannelAllowExportWrapper
    }

    private _parseEntityTemplate(c?: Partial<ChannelModel>) {
        this.productEntityTemplate = null;

        if (Array.isArray(c?.entityTemplate)) {
            this.productEntityTemplate = c?.entityTemplate.find((e) => e.entity === EntityEnum.Product) ?? null;
        }
        
        if (!!this.productEntityTemplate) {
            Object.keys(this.productEntityTemplate.fields).forEach((keyEntityTemplate) => {
                Object.keys(productEntityTemplateFields).forEach((keyEntityTemplateOption) => {
                    this.productEntityTemplate!.fields[keyEntityTemplate] = this.productEntityTemplate!.fields[keyEntityTemplate]
                        .replace(productEntityTemplateFields[keyEntityTemplateOption].enum, productEntityTemplateFields[keyEntityTemplateOption].name)
                })
            })
        } else {
            this.productEntityTemplate = {
                entity: EntityEnum.Product,
                fields: {
                    name: productEntityTemplateFields.name.name,
                    description: productEntityTemplateFields.description.name,
                    descriptionShort: productEntityTemplateFields.descriptionShort.name,
                    descriptionLong: productEntityTemplateFields.descriptionLong.name,
                    seoTitle: productEntityTemplateFields.seoTitle.name,
                    seoKeywords: productEntityTemplateFields.seoKeywords.name,
                    seoDescription: productEntityTemplateFields.seoDescription.name,
                }
            }
        }
    }
}
